import { Button, ConfigProvider, List, Select, message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import {
  OrganizationUsersResponse,
  USER_STATE_IN_ORGANIZATION,
  UserInvitation,
  activateOrgUser,
  deactivateOrgUser,
  getOrganizationUsers,
  removeInvitation,
  resendInvitation,
  updateInvitation,
  updateUserRole,
} from '../../../../api/organization';
import { inviteUsersToOrganization } from '../../../../api/user';
import { OrganizationAccessRoles, getCurrentlyActiveOrganization } from '../../../../common/auth';
import { getLabelForUserStateInOrganization, getUserInitials } from '../../../../common/userHelper';
import { getProfilePictureUrl } from '../../../../common/utils';
import InvitationModal from '../../../../components/InvitationModal/InvitationModal';
import { useGetOrganizationInvites, useGetOrganizationUsers } from '../../../../serverState/organization';
import { useGetUserFromAccessTokenOld } from '../../../../serverState/user';

import { queryClient } from '../../../../queryClient';
import UserManagementItem from './UserManagementItem';

export type NewUserManagerProps = {};

export type UM_User = {
  id: number;
  name: string;
  email: string;
  profileUrl: string;
  status: string;
  joined: Date | string;
};

const HEADER_SIZE = 76;

export const NewUserManager = (props: NewUserManagerProps) => {
  const {
    inviteModalOpen,
    setInviteModalOpen,
    queryUsers,
    queryInvites,
    currentUserOrgAccess,
    contextHolder,
    handleMenuClick,
    handleInviteSubmit,
    getItems,
    getUserRoleComponent,
  } = useOrgUserManagement();

  const mergedData = [
    ...(queryUsers.data || []).map((user) => ({ ...user, type: 'user' })),
    ...(queryInvites.data || []).map((invite) => ({
      ...invite,
      type: 'invite',
    })),
  ].sort((a, b) => {
    // 1. Sort by status: active > pending > deactivated
    const getStatusPriority = (item) => {
      if (item.type === 'invite') return 1; // pending
      if (item.usersStateInOrganization === USER_STATE_IN_ORGANIZATION.ACTIVE) return 0; // active
      return 2; // deactivated
    };

    const statusDiff = getStatusPriority(a) - getStatusPriority(b);
    if (statusDiff !== 0) return statusDiff;

    // 2. Sort by role: admins first, then members
    const getRolePriority = (item) => {
      const role = item.type === 'user' ? item.roleToAssume : item.invitedAsRole;
      return role === OrganizationAccessRoles.ADMIN ? 0 : 1;
    };

    const roleDiff = getRolePriority(a) - getRolePriority(b);
    if (roleDiff !== 0) return roleDiff;

    // 3. Sort by name A-Z (or email if no name)
    const getName = (item) => {
      if (item.type === 'user') {
        return item.User.fullName || item.User.email;
      }
      return item.email;
    };

    const nameA = getName(a).toLowerCase();
    const nameB = getName(b).toLowerCase();
    const nameDiff = nameA.localeCompare(nameB);
    if (nameDiff !== 0) return nameDiff;

    // 4. Sort by join date (oldest first)
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateA - dateB;
  });

  return (
    <div className="flex flex-col items-center">
      {/* <OrgInvitationModal isOpen={true} onCancel={() => { }} /> */}
      {contextHolder}
      {inviteModalOpen && (
        <InvitationModal
          open={inviteModalOpen}
          onOk={handleInviteSubmit}
          onCancel={() => {
            setInviteModalOpen(false);
          }}
        />
      )}

      <div className="user-management-page">
        <div style={{ height: HEADER_SIZE + 'px' }} className="flex justify-between items-center">
          <div className="flex gap-2">
            <div className="title-section">Organization Members</div>
            <span className="items-counter">
              <span className="counter-label">{mergedData.length}</span>
            </span>
          </div>
          {currentUserOrgAccess?.roleToAssume === OrganizationAccessRoles.ADMIN && (
            <Button
              type={'primary'}
              onClick={() => {
                setInviteModalOpen(true);
              }}
              size={'large'}
            >
              Invite team members
            </Button>
          )}
        </div>

        <ConfigProvider
          theme={{
            components: {
              List: {
                titleMarginBottom: 0,
              },
            },
          }}
        >
          <List
            className={'min-h-[100px] '}
            loading={queryUsers.isLoading || queryInvites.isLoading}
            style={{ paddingBlock: '0' }}
            rowKey={(item) => (item.type === 'user' ? item.User.id : item.id)}
            itemLayout="horizontal"
            dataSource={mergedData}
            header={
              <div className={'flex items-center justify-between bg-neutral !p-2 !pl-5 !pr-5 '}>
                <div className="flex-[3_3_0%] items-start max-w-full  flex-1 text-sm font-medium overflow-hidden text-ellipsis">
                  Name
                </div>
                <div
                  className="flex-1 items-start max-w-full flex-1 text-sm font-medium overflow-hidden 
                                    text-center
                                text-ellipsis"
                >
                  User role
                </div>
                <div
                  className="flex-1 items-start max-w-full flex-1 text-sm font-medium overflow-hidden 
                                    text-center
                                text-ellipsis"
                >
                  Status
                </div>
                <div
                  className="flex-1 items-start max-w-full flex-1  text-sm font-medium overflow-hidden 
                                    text-center
                                text-ellipsis"
                >
                  Joined
                </div>
                <div className="flex items-center max-w-full flex-[0_0_auto] ms-12 min-w-[40px] justify-center text-sm font-medium"></div>
              </div>
            }
            renderItem={(item) => (
              <UserManagementItem
                item={item}
                currentUserOrgAccess={currentUserOrgAccess}
                getItems={getItems}
                handleMenuClick={handleMenuClick}
                getUserRoleComponent={getUserRoleComponent}
                getProfilePictureUrl={getProfilePictureUrl}
                getUserInitials={getUserInitials}
                getLabelForUserStateInOrganization={getLabelForUserStateInOrganization}
              />
            )}
          />
        </ConfigProvider>
      </div>
    </div>
  );
};

export const useOrgUserManagement = () => {
  const queryUsers = useGetOrganizationUsers();
  const queryInvites = useGetOrganizationInvites();

  const tokenUserData = useGetUserFromAccessTokenOld();
  const [currentUserOrgAccess, setcurrentUserOrgAccess] = useState<OrganizationUsersResponse | null>(null);
  useEffect(() => {
    const usersOrgAccess = queryUsers.data?.find((user) => user.User.id === tokenUserData?.user?.user.id);
    setcurrentUserOrgAccess(usersOrgAccess);
  }, [queryUsers.data]);

  const [messageApi, contextHolder] = message.useMessage();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const handleMenuClick = async (e: { key: string }, item: OrganizationUsersResponse | UserInvitation) => {
    const organization = await getCurrentlyActiveOrganization();

    try {
      switch (e.key) {
        case 'deactivateUser': {
          const { data } = await deactivateOrgUser(item.User.id, organization.id);

          await queryClient.invalidateQueries((queryKey) => {
            return queryKey.includes('workspaceAccess');
          });
          setTimeout(() => {
            queryUsers.refetch();
          }, 200);

          void messageApi.success(`Successfully deactivated user ${item.User.fullName || item.User.email}`);
          break;
        }
        case 'activateUser': {
          const { data } = await activateOrgUser(item.User.id, organization.id);

          await queryClient.invalidateQueries((queryKey) => {
            return queryKey.includes('workspaceAccess');
          });
          setTimeout(() => {
            queryUsers.refetch();
          }, 200);

          void messageApi.success(`Successfully activated user ${item.User.fullName || item.User.email}`);
          break;
        }
        case 'removeInvitation': {
          await removeInvitation(item.id, organization.id);
          setTimeout(() => {
            queryInvites.refetch();
          }, 200);

          void messageApi.success(`Successfully removed invitation for ${item.email}`);
          break;
        }
        case 'resendInvitation': {
          await resendInvitation(item.id, organization.id);
          setTimeout(() => {
            queryInvites.refetch();
          }, 200);

          void messageApi.success(`Successfully resent invitation for ${item.email}`);
          break;
        }
        default: {
          void messageApi.error('Invalid action');
        }
      }
    } catch (e: any) {
      if (e.response.status >= 400 && e.response.status < 500) {
        void messageApi.error(e.response.data.error || e.response.data);
      } else {
        void message.error('Something went wrong. Please try again later.');
      }
    }
  };

  const handleInviteSubmit = async (invites: Array<{ email: string; role: OrganizationAccessRoles }>) => {
    try {
      // Pass users with roles to the new API
      const invReq = await inviteUsersToOrganization(invites);

      void messageApi.success('Users invited successfully.');

      queryUsers.refetch();
      queryInvites.refetch();

      setInviteModalOpen(false);
    } catch (e: any) {
      console.log(e);
      const errorCode = e?.response?.data?.code;
      const errorMessage = e?.response?.data?.message;

      if (errorCode === 'ERR-USER-33') {
        void messageApi.error('Only admins can invite other admins');
      } else if (errorCode === 'ERR-USER-34') {
        void messageApi.error('Please provide valid user information');
      } else if (errorCode === 'ERR-USER-35') {
        void messageApi.error('Invalid role specified');
      } else {
        void messageApi.error(errorMessage || 'Failed to invite users.');
      }
    }
  };

  const handleUserRoleChange = async (role: OrganizationAccessRoles, user: OrganizationUsersResponse) => {
    try {
      const res = await updateUserRole(user.User.id, (await getCurrentlyActiveOrganization()).id, role);

      void messageApi.success(`${user.User.fullName || user.User.email} role updated to ${role}.`);

      // Fetch the list of users again.
    } catch (e: any) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (e.response.status >= 400 && e.response.status < 500) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (e.response.data.code === 'ERR-ORG-5') {
          void message.warning('Unable to change role of non-active user.');
        } else {
          void message.error(e.response.data);
        }
      } else {
        void message.error(e.message);
        console.log(e);
      }
    } finally {
      setTimeout(() => {
        queryUsers.refetch();
      }, 100);
    }
  };

  const handleInviteRoleChange = async (role: OrganizationAccessRoles, invite: UserInvitation) => {
    try {
      const res = await updateInvitation(invite.id, (await getCurrentlyActiveOrganization()).id, role);

      void messageApi.success(`${invite.email} invitation role updated to ${role}.`);
    } catch (e: any) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (e.response.status >= 400 && e.response.status < 500) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        void message.error(e.response.data);
      } else {
        void message.error(e.message);
        console.log(e);
      }
    } finally {
      setTimeout(() => {
        queryInvites.refetch();
      }, 100);
    }
  };

  const getItems = useCallback(
    (itemInTable: OrganizationUsersResponse | UserInvitation) => {
      const isUser = itemInTable.type === 'user';

      if (isUser) {
        const item = itemInTable as OrganizationUsersResponse;
        switch (item.usersStateInOrganization) {
          case USER_STATE_IN_ORGANIZATION.INACTIVE: {
            return [
              {
                label: 'Activate User',
                key: 'activateUser',
              },
            ];
          }
          case USER_STATE_IN_ORGANIZATION.ACTIVE: {
            return [
              {
                label: 'Deactivate User',
                key: 'deactivateUser',
                danger: true,
              },
            ];
          }
        }
      } else {
        return [
          {
            label: 'Cancel invitation',
            key: 'removeInvitation',
          },
          {
            label: 'Resend invitation',
            key: 'resendInvitation',
          },
        ];
      }
    },
    [queryUsers.data]
  );

  const getUserRoleComponent = (itemInTable: OrganizationUsersResponse | UserInvitation) => {
    // Owner cannot be changed from this page.
    const isUser = itemInTable.type === 'user';
    return (
      <Select
        onSelect={(e) =>
          isUser
            ? handleUserRoleChange(e as OrganizationAccessRoles, itemInTable)
            : handleInviteRoleChange(e as OrganizationAccessRoles, itemInTable)
        }
        className={'h-[3em]'}
        value={isUser ? itemInTable.roleToAssume : itemInTable.invitedAsRole}
        bordered={false}
        disabled={currentUserOrgAccess?.roleToAssume === OrganizationAccessRoles.MEMBER}
      >
        <Select.Option value={OrganizationAccessRoles.ADMIN}>Admin</Select.Option>
        <Select.Option value={OrganizationAccessRoles.MEMBER}>Member</Select.Option>
      </Select>
    );
  };

  return {
    queryUsers,
    queryInvites,
    currentUserOrgAccess,
    contextHolder,
    inviteModalOpen,
    setInviteModalOpen,
    handleMenuClick,
    handleInviteSubmit,
    handleUserRoleChange,
    getItems,
    getUserRoleComponent,
  };
};
